import { useEffect } from 'react'

const ContactDrawer = ({ isOpen, onClose }) => {
  // Close on ESC key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  // Prevent background scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className={`fixed inset-0 bg-slate-900/50 backdrop-blur-xs transition-opacity duration-300 z-50 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Slide-out Panel */}
      <div 
        data-lenis-prevent
        className={`fixed top-0 right-0 h-full w-full sm:w-[460px] bg-[#FAF6F2] shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-in-out transform border-l border-slate-200/60 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Drawer Header */}
        <div className="px-6 py-5 border-b border-slate-200/80 bg-white flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-slate-900 leading-tight not-italic">
              Get in Touch
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1">
              Direct helplines, rescue support &amp; vet consultations
            </p>
          </div>
          <button 
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-full transition-all focus:outline-none cursor-pointer"
            aria-label="Close drawer"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5 no-scrollbar [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          
          {/* 1. Direct Helplines & Contact Details Card */}
          <div className="bg-white p-5 sm:p-6 rounded-2xl border border-slate-200/70 shadow-xs space-y-4 text-left">
            <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
              <div className="w-8 h-8 rounded-xl bg-brand-orange-50 text-brand-orange-700 flex items-center justify-center shrink-0">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base text-slate-900 font-bold">Direct CAWT Helplines</h3>
                <p className="text-[11px] text-slate-500 font-medium">Connect directly with our team</p>
              </div>
            </div>

            <div className="space-y-3.5 text-xs">
              {/* Primary Helpline */}
              <div className="flex items-center justify-between py-2 border-b border-slate-100/80">
                <div>
                  <span className="block text-[10px] uppercase font-bold text-slate-400 tracking-wider">Primary Helpline</span>
                  <a href="tel:+918123038270" className="font-mono font-bold text-sm text-brand-orange-700 hover:underline">
                    +91 81230 38270
                  </a>
                </div>
                <a 
                  href="tel:+918123038270"
                  className="px-3.5 py-1.5 bg-brand-orange-50 text-brand-orange-700 font-bold rounded-xl hover:bg-brand-orange-100 transition-colors border border-brand-orange-200/60"
                >
                  Call
                </a>
              </div>

              {/* Secondary Helpline */}
              <div className="flex items-center justify-between py-2 border-b border-slate-100/80">
                <div>
                  <span className="block text-[10px] uppercase font-bold text-slate-400 tracking-wider">Secondary Support</span>
                  <a href="tel:+919035999372" className="font-mono font-bold text-sm text-brand-orange-700 hover:underline">
                    +91 90359 99372
                  </a>
                </div>
                <a 
                  href="tel:+919035999372"
                  className="px-3.5 py-1.5 bg-brand-orange-50 text-brand-orange-700 font-bold rounded-xl hover:bg-brand-orange-100 transition-colors border border-brand-orange-200/60"
                >
                  Call
                </a>
              </div>

              {/* Email */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <span className="block text-[10px] uppercase font-bold text-slate-400 tracking-wider">Official Trust Email</span>
                  <a href="mailto:cawtblr@gmail.com" className="font-bold text-slate-800 hover:underline break-all text-xs">
                    cawtblr@gmail.com
                  </a>
                </div>
                <a 
                  href="mailto:cawtblr@gmail.com"
                  className="px-3.5 py-1.5 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-colors border border-slate-200"
                >
                  Email
                </a>
              </div>
            </div>
          </div>

          {/* 2. Online Veterinary Consultation Card */}
          <div className="p-5 bg-white border border-slate-200/70 rounded-2xl text-left space-y-3 shadow-xs">
            <div className="flex items-center gap-2 text-slate-900 font-bold">
              <h3 className="text-base text-slate-900 font-bold">Online Veterinary Consultation</h3>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed font-medium">
              Need immediate expert guidance for your pet or community animal? Consult qualified vets on <strong className="text-brand-orange-700">farmvet.in</strong> or via WhatsApp.
            </p>
            <div className="flex flex-wrap gap-2.5 pt-1">
              <a 
                href="https://farmvet.in" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-4 py-2.5 bg-brand-orange-700 hover:bg-brand-orange-800 text-white font-bold text-xs rounded-xl transition-colors inline-flex items-center gap-1.5 shadow-xs cursor-pointer"
              >
                <span>Visit farmvet.in</span>
                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              <a 
                href="https://wa.me/918123038270?text=I%20need%20a%20vet%20consultation%20for%20an%20animal" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl transition-colors inline-flex items-center gap-1.5 shadow-xs cursor-pointer"
              >
                <span>WhatsApp</span>
                <svg className="w-3.5 h-3.5 fill-current text-white" viewBox="0 0 24 24">
                  <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91C2.13 13.66 2.59 15.36 3.45 16.86L2.05 22L7.3 20.62C8.75 21.41 10.38 21.83 12.04 21.83C17.5 21.83 21.95 17.38 21.95 11.92C21.95 9.27 20.92 6.78 19.05 4.91C17.18 3.03 14.69 2 12.04 2M12.04 3.67C14.25 3.67 16.31 4.53 17.87 6.09C19.42 7.65 20.28 9.72 20.28 11.92C20.28 16.46 16.58 20.15 12.04 20.15C10.56 20.15 9.11 19.76 7.85 19L7.55 18.83L4.43 19.65L5.26 16.61L5.06 16.29C4.24 15 3.8 13.47 3.8 11.91C3.81 7.37 7.5 3.67 12.04 3.67M9.53 7.35C9.36 7.35 9.17 7.36 9 7.36C8.77 7.36 8.5 7.45 8.27 7.7C8.04 7.95 7.42 8.54 7.42 9.74C7.42 10.94 8.29 12.1 8.41 12.26C8.53 12.42 10.13 14.88 12.58 15.94C14.62 16.82 15.03 16.64 15.48 16.6C15.93 16.56 16.93 16 17.14 15.42C17.35 14.84 17.35 14.34 17.29 14.24C17.23 14.14 17.07 14.08 16.82 13.96C16.57 13.84 15.35 13.24 15.12 13.16C14.89 13.08 14.73 13.04 14.56 13.28C14.4 13.53 13.92 14.08 13.78 14.24C13.63 14.4 13.49 14.42 13.24 14.3C12.99 14.18 12.2 13.92 11.26 13.08C10.53 12.43 10.04 11.62 9.9 11.37C9.75 11.12 9.88 10.99 10.01 10.87C10.12 10.76 10.26 10.58 10.38 10.43C10.5 10.28 10.55 10.18 10.63 10.01C10.71 9.85 10.67 9.7 10.61 9.58C10.55 9.46 10.06 8.26 9.85 7.77C9.65 7.29 9.45 7.36 9.3 7.35H9.53Z" />
                </svg>
              </a>
            </div>
          </div>

        </div>

        {/* Drawer Footer */}
        <div className="p-4 border-t border-slate-200 bg-white flex items-center justify-between text-xs text-slate-400 font-semibold px-6 shrink-0">
          <span>Chetak Animal Welfare Trust</span>
          <span className="flex items-center gap-1 text-slate-500">
            <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Registered Trust
          </span>
        </div>
      </div>
    </>
  )
}

export default ContactDrawer
